import { DatePicker, Tooltip, message } from "antd";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import EmojiPicker from 'emoji-picker-react';
import moment from "moment";
import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import ProjectServices from "../../../Services/ProjectServices";
import DataParser from "../../../utlits/Dataparser";
import UserServices from "../../../Services/UserService";
import SocketService from "../../../Services/SocketService";
import { FaProjectDiagram } from "react-icons/fa";

const CreateProject = (props) => {
    const {
        id,
        labelledby,
        reFreshData,
        closeBtnId,
        onProjectCreated,
    } = props;

    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const quillRef = useRef(null);
    const clickedLinkRef = useRef({ index: null, length: null });
    const descBlurTimeoutRef = useRef(null);
    const [name, setName] = useState("");
    const [users, setUsers] = useState([]);
    const [owner, setOwner] = useState(null);
    const [status, setStatus] = useState("Planning");
    const [description, setDescription] = useState("");
    const [dueDate, setDueDate] = useState(moment());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [showAllAttachments, setShowAllAttachments] = useState(false);

    // Description field states
    const [isDescFocused, setIsDescFocused] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [linkUrl, setLinkUrl] = useState("");

    const companyId = DataParser.CompanyId();

    // Modal-style dropdowns
    const [isOwnerMenuOpen, setIsOwnerMenuOpen] = useState(false);
    const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
    const [userSearchTerm, setUserSearchTerm] = useState("");

    const filteredUserOptions = users
        .map(user => ({ value: user.id, label: user.full_name || `${user.first_name || ""} ${user.last_name || ""}`.trim() }))
        .filter(option => option.label.toLowerCase().includes(userSearchTerm.toLowerCase()))
        .slice(0, 5);

    useEffect(() => {
        const fetchUsers = () => {
            UserServices.getProjectUsers(companyId)
                .then((res) => {
                    if (res.Good) {
                        setUsers(res.data.user || []);
                    }
                })
                .catch((err) => console.error("Error fetching users:", err));
        };
        fetchUsers();

        const handleClickOutside = (event) => {
            if (!event.target.closest('.owner-select-container')) setIsOwnerMenuOpen(false);
            if (!event.target.closest('.status-select-container')) setIsStatusMenuOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [companyId]);

    const resetForm = () => {
        setName("");
        setOwner(null);
        setStatus("Planning");
        setDescription("");
        setDueDate(moment());
        setAttachments([]);
        setIsSubmitting(false);
        setShowEmojiPicker(false);
        setShowLinkInput(false);
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setAttachments([...attachments, ...files]);
        e.target.value = null;
    };

    const removeAttachment = (index) => {
        setAttachments(attachments.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        if (!name.trim()) {
            message.error("Project name is required");
            return;
        }

        if (!dueDate) {
            message.error("Due date is required for a project");
            return;
        }

        setIsSubmitting(true);
        const body = {
            name,
            owner: owner ? owner.value : null,
            status,
            description,
            company_id: companyId,
            due_date: dueDate ? dueDate.toISOString() : null,
        };

        try {
            const res = await ProjectServices.createProject(body);
            if (res.Good) {
                const newProjectId = res.data.id;

                if (attachments.length > 0) {
                    for (const file of attachments) {
                        const formData = new FormData();
                        formData.append("file", file);
                        await ProjectServices.uploadAttachment(newProjectId, formData, { headers: { "Content-Type": "multipart/form-data" } });
                    }
                }

                message.success("Project created successfully!");
                // Notify sidebar and other tabs about the new project
                SocketService.emit("project_update", { company_id: companyId, action: "create" });
                window.dispatchEvent(new CustomEvent("planit:projectsUpdated", { detail: { company_id: companyId } }));
                const closeBtn = document.getElementById(closeBtnId || "projectClose");
                if (closeBtn) closeBtn.click();
                if (onProjectCreated) onProjectCreated(newProjectId, name);
                if (reFreshData) reFreshData();
                resetForm();
            } else {
                message.error(res.data || "Failed to create project");
            }
        } catch (err) {
            console.error("Project creation failed:", err);
            message.error("An error occurred");
        } finally {
            setIsSubmitting(false);
        }
    };

    return ReactDOM.createPortal(
        <div
            className="modal fade"
            id={id}
            tabIndex={-1}
            aria-labelledby={labelledby}
            aria-hidden="true"
        >
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "850px" }}>
                <div className="modal-content" style={{ borderRadius: "24px", border: "none", padding: "10px 18px 20px 18px", boxShadow: "0 10px 40px rgba(0,0,0,0.12)" }}>
                    <div className="modal-header border-0 pb-2 d-flex justify-content-end" style={{ paddingBottom: '8px' }}>
                        <button
                            type="button"
                            className="btn-close shadow-none"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            id={closeBtnId || "projectClose"}
                            onClick={resetForm}
                            style={{ fontSize: '18px', backgroundSize: '16px', opacity: 0.8 }}
                        />
                    </div>

                    <div className="modal-body pt-0 px-4 pb-1">
                        <style>{`
                .project-title-float-wrap {
                    position: relative;
                    margin-bottom: 8px;
                }
                .project-title-float-wrap input {
                    width: 100%;
                    border-radius: 12px !important;
                    height: 44px !important;
                    padding: 0 16px !important;
                    font-size: 17px !important;
                    font-weight: 500 !important;
                    border: 1.5px solid transparent !important;
                    transition: all 0.2s ease !important;
                    background: transparent;
                    outline: none;
                }
                .project-title-float-wrap input::placeholder {
                    color: #a0a0a0 !important;
                    font-size: 17px !important;
                    font-weight: 500 !important;
                }
                .project-title-float-wrap input:hover, 
                .project-title-float-wrap input:focus { 
                    border: 1.5px solid #1D1D1D !important; 
                    box-shadow: none !important;
                    outline: none !important;
                }
                .project-title-float-wrap label {
                    position: absolute;
                    left: 16px !important;
                    top: 50% !important;
                    transform: translateY(-50%);
                    font-size: 17px !important;
                    opacity: 0 !important; 
                    transition: all 0.2s ease !important;
                    pointer-events: none;
                    background: #fff;
                    padding: 0 5px;
                    color: #1D1D1D;
                    z-index: 10;
                }
                /* Show label ONLY when focused */
                .project-title-float-wrap input:focus ~ label {
                    top: 0 !important;
                    font-size: 12px !important;
                    opacity: 1 !important;
                    transform: translateY(-50%);
                }
                .project-title-float-wrap input:focus::placeholder {
                    color: transparent !important;
                }

                .project-desc-float-wrap {
                    position: relative;
                    border-radius: 12px;
                    border: 1.5px solid transparent;
                    transition: all 0.2s ease;
                    margin-bottom: 12px;
                }
                .project-desc-float-wrap:hover, .project-desc-float-wrap.focused { 
                    border-color: #1D1D1D !important; 
                }

                .project-desc-float-wrap .desc-icon {
                    position: absolute;
                    left: 16px;
                    top: 13px !important;
                    transform: none !important;
                    color: #a0a0a0;
                    transition: all 0.2s ease;
                    pointer-events: none;
                    z-index: 10;
                    opacity: 1;
                }
                .project-desc-float-wrap.focused .desc-icon,
                .project-desc-float-wrap.has-value .desc-icon {
                    opacity: 0 !important;
                }

                .project-desc-float-wrap label {
                    position: absolute;
                    left: 48px;
                    top: 12px !important;
                    transform: none !important;
                    font-size: 17px !important;
                    line-height: 20px !important;
                    color: #a0a0a0;
                    transition: all 0.2s ease !important;
                    pointer-events: none;
                    background: #fff !important;
                    padding: 0 5px;
                    z-index: 99 !important;
                    opacity: 1;
                }
                .project-desc-float-wrap.focused label,
                .project-desc-float-wrap.has-value label {
                    top: 0 !important;
                    transform: translateY(-50%) !important;
                    left: 12px !important;
                    font-size: 12px !important;
                    opacity: 1 !important;
                    color: #1D1D1D !important;
                    background: #fff !important;
                    z-index: 99 !important;
                }
                .project-desc-float-wrap.has-value:not(.focused) label {
                    opacity: 0 !important;
                    visibility: hidden !important;
                    pointer-events: none !important;
                }

                .ql-editor {
                    padding: 12px 16px 8px 48px !important;
                    min-height: 44px !important;
                    max-height: 200px !important;
                    overflow-y: auto !important;
                    font-size: 15px !important;
                    transition: all 0.2s ease;
                }
                .project-desc-float-wrap.focused .ql-editor,
                .project-desc-float-wrap.has-value .ql-editor,
                .project-desc-float-wrap .ql-editor:not(.ql-blank) {
                    padding-left: 16px !important;
                }
                .ql-editor.ql-blank::before {
                    display: none !important;
                }
                .ql-container.ql-snow {
                    border: none !important;
                    font-family: inherit !important;
                }

                .desc-toolbar {
                    display: flex;
                    gap: 4px;
                    background: #F5F5F5;
                    padding: 2px 4px;
                    border-radius: 6px;
                    width: fit-content;
                    margin: 0 0 6px 16px;
                    opacity: 0;
                    visibility: hidden;
                    transition: all 0.2s ease;
                }
                .project-desc-float-wrap.focused .desc-toolbar {
                    opacity: 1;
                    visibility: visible;
                }
                .toolbar-btn {
                    background: transparent !important;
                    border: none !important;
                    padding: 0 !important;
                    border-radius: 4px;
                    color: #1D1D1D !important;
                    font-size: 14px !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    transition: all 0.2s ease;
                    cursor: pointer;
                    width: 26px !important;
                    height: 26px !important;
                }
                .toolbar-btn i {
                    font-size: 14px !important;
                }
                .toolbar-btn:hover {
                    background: #E8E8E8 !important;
                }

                .action-btn-bar {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 12px;
                    margin-bottom: 24px;
                }
                .action-btn-wrap {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 4px 12px;
                    background: #fff;
                    border: 1px solid #E8E8E8;
                    border-radius: 6px;
                    min-height: 30px;
                    height: 30px;
                    transition: all 0.2s;
                    user-select: none;
                    color: #A0A0A0;
                }
                .action-btn-wrap:hover, .action-btn-wrap.active {
                    border-color: #1D1D1D !important;
                    color: #1D1D1D !important;
                    background: #fff;
                }
                .action-btn-wrap:hover .action-icon, .action-btn-wrap.active .action-icon {
                    color: #1D1D1D !important;
                }
                .action-icon {
                    font-size: 14px;
                    color: inherit !important;
                    display: flex;
                    align-items: center;
                }

                .custom-user-dropdown {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    width: 250px;
                    background: #fff;
                    border: 1px solid #E8E8E8;
                    border-radius: 10px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                    z-index: 10000;
                    margin-top: 5px;
                    overflow: hidden;
                }
                .search-header {
                    padding: 12px 16px;
                    border-bottom: 1px solid #F0F0F0;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .search-header i {
                    font-size: 18px;
                    color: #1a1a1a;
                }
                .search-header input {
                    border: none !important;
                    outline: none !important;
                    box-shadow: none !important;
                    font-size: 14px;
                    width: 100%;
                    color: #1D1D1D;
                    font-weight: 400;
                }
                .user-list-scroll {
                    padding: 5px;
                    max-height: 175px !important;
                    overflow-y: auto;
                }
                .user-option-item {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 8px 10px;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    font-size: 11px;
                    color: #1a1a1a;
                }
                .user-option-item:hover {
                    background: #F5F5F5;
                }
                .user-avatar-mini {
                    width: 20px;
                    height: 20px;
                    background-color: #0DDFAB;
                    color: #fff;
                    border-radius: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 9px;
                    font-weight: 700;
                }

                .attachment-chip {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 4px 12px;
                    background: #1D1D1D;
                    color: #0DDFAB;
                    border-radius: 5px;
                    font-size: 12px;
                    font-weight: 600;
                }
                
                .action-datepicker .ant-picker-input > input {
                    font-size: 13px !important;
                    font-weight: 600 !important;
                    color: #1D1D1D !important;
                    cursor: pointer !important;
                }
                .action-datepicker .ant-picker-input > input::placeholder {
                    color: #A0A0A0 !important;
                }

                .link-popover {
                    background: #fff;
                    border: 1.5px solid #1D1D1D;
                    border-radius: 10px;
                    padding: 4px;
                    bottom: 50px !important;
                    left: 10px !important;
                    width: 320px;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
                    z-index: 10001;
                }
                
                /* Custom Dropdown Panel for Status */
                .custom-dropdown-panel {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    width: 180px;
                    background: #fff;
                    border-radius: 12px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                    border: 1px solid #E8E8E8;
                    margin-top: 8px;
                    z-index: 1000;
                    padding: 8px;
                }
                .option-list {
                    max-height: 200px;
                    overflow-y: auto;
                }
                .option-item {
                    padding: 8px 12px;
                    border-radius: 6px;
                    font-size: 11px;
                    color: #1D1D1D;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .attachment-trigger {
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #fff;
                    border-radius: 6px;
                    transition: all 0.2s ease;
                    border: 1px solid #E8E8E8;
                }
                .attachment-trigger:hover { background: #f0f1f2; border-color: #adb5bd; }

                .btn-create-project {
                    background-color: #0DDFAB;
                    border: none;
                    border-radius: 5px;
                    height: 30px;
                    padding: 0 20px;
                    color: #fff;
                    font-weight: 700;
                    font-size: 13px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    box-shadow: none !important;
                    flex-shrink: 0 !important;
                }
                .btn-create-project:hover {
                    background-color: #0bc698;
                    transform: translateY(-2px);
                }
                .btn-create-project:active { transform: translateY(0); }
                .btn-create-project:disabled { opacity: 0.6; cursor: not-allowed; transform: none; box-shadow: none; }

                .attachment-preview-chip {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    background: #1D1D1D;
                    color: #0DDFAB;
                    padding: 0 8px;
                    height: 24px;
                    border-radius: 4px;
                    font-size: 11px;
                    font-weight: 500;
                    transition: all 0.2s ease;
                }
                .chip-file-name {
                    max-width: 150px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .chip-remove-icon {
                    font-size: 12px;
                    color: #0DDFAB;
                    cursor: pointer;
                    transition: transform 0.2s;
                }
            `}</style>

                        <form onSubmit={handleSubmit}>
                            <div className="project-title-float-wrap">
                                <input
                                    type="text"
                                    id="projectTitleInput"
                                    placeholder="Project Name"
                                    value={name}
                                    onChange={(e) => {
                                        setName(e.target.value);
                                    }}
                                    autoFocus
                                />
                                <label htmlFor="projectTitleInput">Project Name</label>
                            </div>

                            {/* Description Section */}
                            <div
                                className={`project-desc-float-wrap ${isDescFocused || showLinkInput || showEmojiPicker ? 'focused' : ''} ${(description && description !== '<p><br></p>') ? 'has-value' : ''}`}
                                onClick={(e) => {
                                    const anchor = e.target.closest('a');
                                    if (anchor && anchor.closest('.ql-editor')) {
                                        e.preventDefault();
                                        const url = anchor.getAttribute('href');
                                        setLinkUrl(url);
                                        setShowLinkInput(true);

                                        const q = quillRef.current.getEditor();
                                        const Quill = ReactQuill.Quill;
                                        const blot = Quill.find(anchor);
                                        if (blot) {
                                            clickedLinkRef.current = {
                                                index: q.getIndex(blot),
                                                length: blot.length()
                                            };
                                        }
                                    }
                                }}
                            >
                                <ReactQuill
                                    ref={quillRef}
                                    theme="snow"
                                    placeholder=""
                                    value={description}
                                    onChange={setDescription}
                                    onFocus={() => {
                                        if (descBlurTimeoutRef.current) {
                                            clearTimeout(descBlurTimeoutRef.current);
                                            descBlurTimeoutRef.current = null;
                                        }
                                        setIsDescFocused(true);
                                    }}
                                    onBlur={() => {
                                        descBlurTimeoutRef.current = setTimeout(() => {
                                            setIsDescFocused(false);
                                        }, 150);
                                    }}
                                    modules={{ toolbar: false }}
                                />
                                <span className="desc-icon" style={{ display:'inline-block', width:'18px', height:'18px', flexShrink:0, backgroundColor: '#a0a0a0', mask:"url('/assets/images/descriptionmodal.svg') no-repeat center / contain", WebkitMask:"url('/assets/images/descriptionmodal.svg') no-repeat center / contain" }} />
                                <label>Description</label>

                                {/* Custom Toolbar */}
                                <div className="desc-toolbar" onMouseDown={(e) => e.preventDefault()}>
                                    <button type="button" className="toolbar-btn" onClick={() => {
                                        const quill = quillRef.current.getEditor();
                                        const format = quill.getFormat();
                                        quill.format('bold', !format.bold);
                                    }}>
                                        <i className="uil uil-bold" />
                                    </button>
                                    <button type="button" className="toolbar-btn" onClick={() => {
                                        const quill = quillRef.current.getEditor();
                                        const format = quill.getFormat();
                                        quill.format('italic', !format.italic);
                                    }}>
                                        <i className="uil uil-italic" />
                                    </button>
                                    <button type="button" className="toolbar-btn" onClick={() => {
                                        const quill = quillRef.current.getEditor();
                                        const format = quill.getFormat();
                                        quill.format('underline', !format.underline);
                                    }}>
                                        <i className="uil uil-underline" />
                                    </button>
                                    <div className="position-relative">
                                        <button type="button" className="toolbar-btn" onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                                            <i className="uil uil-smile" />
                                        </button>
                                        {showEmojiPicker && (
                                            <div className="position-absolute" style={{ top: '40px', left: '0', zIndex: 10000 }} onMouseDown={(e) => e.stopPropagation()}>
                                                <EmojiPicker
                                                    onEmojiClick={(emojiData) => {
                                                        const quill = quillRef.current.getEditor();
                                                        const range = quill.getSelection();
                                                        if (range) {
                                                            if (range.length > 0) {
                                                                quill.deleteText(range.index, range.length);
                                                            }
                                                            quill.insertText(range.index, emojiData.emoji);
                                                            quill.setSelection(range.index + emojiData.emoji.length);
                                                        } else {
                                                            quill.insertText(quill.getLength(), emojiData.emoji);
                                                        }
                                                        setShowEmojiPicker(false);
                                                    }}
                                                    width={300}
                                                    height={400}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="position-relative">
                                        <button type="button" className="toolbar-btn" onClick={() => {
                                            const quill = quillRef.current.getEditor();
                                            const format = quill.getFormat();
                                            if (format.link) {
                                                setLinkUrl(format.link);
                                                const range = quill.getSelection();
                                                if (range) {
                                                    const [leaf] = quill.getLeaf(range.index);
                                                    let blot = leaf;
                                                    const Quill = ReactQuill.Quill;
                                                    while (blot && blot.statics.blotName !== 'link' && blot !== quill.scroll) {
                                                        blot = blot.parent;
                                                    }
                                                    if (blot && blot.statics.blotName === 'link') {
                                                        clickedLinkRef.current = {
                                                            index: quill.getIndex(blot),
                                                            length: blot.length()
                                                        };
                                                    } else {
                                                        clickedLinkRef.current = { index: null, length: null };
                                                    }
                                                }
                                            } else {
                                                setLinkUrl("");
                                                clickedLinkRef.current = { index: null, length: null };
                                            }
                                            setShowLinkInput(!showLinkInput);
                                        }}>
                                            <i className="uil uil-link" />
                                        </button>
                                        {showLinkInput && (
                                            <div className="position-absolute link-popover" onMouseDown={(e) => e.stopPropagation()}>
                                                <div className="d-flex gap-2 align-items-center">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Paste or type a link..."
                                                        value={linkUrl}
                                                        onChange={(e) => setLinkUrl(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                const quill = quillRef.current.getEditor();
                                                                const { index, length } = clickedLinkRef.current;
                                                                if (index !== null && length !== null) {
                                                                    quill.formatText(index, length, 'link', linkUrl);
                                                                } else {
                                                                    quill.format('link', linkUrl);
                                                                }
                                                                setShowLinkInput(false);
                                                                setLinkUrl("");
                                                                clickedLinkRef.current = { index: null, length: null };
                                                            }
                                                        }}
                                                    />
                                                    {linkUrl && (
                                                        <a
                                                            href={linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="toolbar-btn d-flex align-items-center justify-content-center"
                                                            style={{ padding: '4px 8px', borderRadius: '6px', border: '1.5px solid #1D1D1D', color: '#1D1D1D', textDecoration: 'none', background: '#fff', width: '32px', height: '32px' }}
                                                            title="Open link in new tab"
                                                        >
                                                            <i className="uil uil-external-link-alt" style={{ fontSize: '18px' }} />
                                                        </a>
                                                    )}
                                                    {linkUrl && (
                                                        <button
                                                            type="button"
                                                            className="toolbar-btn d-flex align-items-center justify-content-center"
                                                            style={{ padding: '4px 8px', borderRadius: '6px', border: '1.5px solid #ff4d4f', color: '#ff4d4f', background: '#fff', width: '32px', height: '32px' }}
                                                            title="Remove link"
                                                            onClick={() => {
                                                                const quill = quillRef.current.getEditor();
                                                                const { index, length } = clickedLinkRef.current;
                                                                if (index !== null && length !== null) {
                                                                    quill.formatText(index, length, 'link', false);
                                                                } else {
                                                                    quill.format('link', false);
                                                                }
                                                                setShowLinkInput(false);
                                                                setLinkUrl("");
                                                                clickedLinkRef.current = { index: null, length: null };
                                                            }}
                                                        >
                                                            <i className="uil uil-link-broken" style={{ fontSize: '18px' }} />
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm"
                                                        style={{ backgroundColor: '#0DDFAB', fontWeight: '600' }}
                                                        onClick={() => {
                                                            const quill = quillRef.current.getEditor();
                                                            const { index, length } = clickedLinkRef.current;
                                                            if (index !== null && length !== null) {
                                                                quill.formatText(index, length, 'link', linkUrl);
                                                            } else {
                                                                quill.format('link', linkUrl);
                                                            }
                                                            setShowLinkInput(false);
                                                            setLinkUrl("");
                                                            clickedLinkRef.current = { index: null, length: null };
                                                        }}
                                                    >
                                                        Apply
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <button type="button" className="toolbar-btn" onClick={() => {
                                        const quill = quillRef.current.getEditor();
                                        const format = quill.getFormat();
                                        quill.format('list', format.list === 'bullet' ? false : 'bullet');
                                    }}>
                                        <i className="uil uil-list-ul" />
                                    </button>
                                </div>
                            </div>

                            <div className="action-btn-bar mt-2">
                                {/* Owner Select */}
                                <div className="owner-select-container position-relative">
                                    <Tooltip title="Assign Owner" placement="top">
                                        <div
                                            className={`action-btn-wrap cursor-pointer ${isOwnerMenuOpen || owner ? 'active' : ''}`}
                                            onClick={() => setIsOwnerMenuOpen(!isOwnerMenuOpen)}
                                        >
                                            <span style={{ display:'inline-block', width:'15px', height:'15px', flexShrink:0, backgroundColor: 'currentColor', mask:"url('/assets/images/assignedtomodal.svg') no-repeat center / contain", WebkitMask:"url('/assets/images/assignedtomodal.svg') no-repeat center / contain", transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)', imageRendering: '-webkit-optimize-contrast' }} />
                                            <span style={{ fontSize: '11px', fontWeight: '600' }}>
                                                {owner ? owner.label : "Assign Owner"}
                                            </span>
                                            <i className={`uil uil-angle-down ms-1`} style={{ fontSize: '14px', transition: 'transform 0.2s', transform: isOwnerMenuOpen ? 'rotate(180deg)' : 'none' }} />
                                        </div>
                                    </Tooltip>

                                    {isOwnerMenuOpen && (
                                        <div className="custom-user-dropdown">
                                            <div className="search-header">
                                                <i className="uil uil-search" />
                                                <input
                                                    placeholder="Search or enter email..."
                                                    value={userSearchTerm}
                                                    onChange={(e) => setUserSearchTerm(e.target.value)}
                                                    autoFocus
                                                />
                                            </div>
                                            <div className="user-list-scroll custom-comment-scroll">
                                                {filteredUserOptions.length > 0 ? filteredUserOptions.map(opt => (
                                                    <div
                                                        key={opt.value}
                                                        className="user-option-item"
                                                        onClick={() => {
                                                            setOwner(opt);
                                                            setIsOwnerMenuOpen(false);
                                                            setUserSearchTerm("");
                                                        }}
                                                    >
                                                        <div className="user-avatar-mini">
                                                            {opt.label.charAt(0)}
                                                        </div>
                                                        <span>{opt.label}</span>
                                                    </div>
                                                )) : <div className="p-3 text-muted text-center" style={{ fontSize: '12px' }}>No members found</div>}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Status Select */}
                                <div className="status-select-container position-relative">
                                    <Tooltip title="Project Status" placement="top">
                                        <div
                                            className={`action-btn-wrap cursor-pointer active`}
                                            onClick={() => setIsStatusMenuOpen(!isStatusMenuOpen)}
                                        >
                                            <FaProjectDiagram className="action-icon" />
                                            <span style={{ fontSize: '11px', fontWeight: '600' }}>
                                                {status}
                                            </span>
                                            <i className={`uil uil-angle-down ms-1`} style={{ fontSize: '14px', transition: 'transform 0.2s', transform: isStatusMenuOpen ? 'rotate(180deg)' : 'none' }} />
                                        </div>
                                    </Tooltip>

                                    {isStatusMenuOpen && (
                                        <div className="custom-dropdown-panel" style={{ width: '180px' }}>
                                            <div className="option-list">
                                                {["Planning", "In Progress", "On Hold", "Completed", "Abandoned"].map(s => (
                                                    <div
                                                        key={s}
                                                        className="option-item"
                                                        onClick={() => {
                                                            setStatus(s);
                                                            setIsStatusMenuOpen(false);
                                                        }}
                                                    >
                                                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: s === 'Planning' ? '#3498db' : s === 'In Progress' ? '#f39c12' : s === 'Completed' ? '#27ae60' : s === 'On Hold' ? '#e74c3c' : '#95a5a6' }} />
                                                        {s}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Due Date Select */}
                                <Tooltip title="Due Date" placement="top">
                                    <div className={`action-btn-wrap position-relative ${dueDate ? 'active' : ''}`}>
                                        <span style={{ display:'inline-block', width:'15px', height:'15px', flexShrink:0, backgroundColor: 'currentColor', mask:"url('/assets/images/duedatemodal.svg') no-repeat center / contain", WebkitMask:"url('/assets/images/duedatemodal.svg') no-repeat center / contain", transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)', imageRendering: '-webkit-optimize-contrast' }} />
                                        <DatePicker
                                            value={dueDate}
                                            onChange={setDueDate}
                                            format="DD MMM"
                                            placeholder="Set Due Date"
                                            className="action-datepicker"
                                            bordered={false}
                                            allowClear={false}
                                            suffixIcon={null}
                                            style={{ padding: 0, width: '90px' }}
                                        />
                                    </div>
                                </Tooltip>

                            </div>

                            {/* Footer Actions */}
                            <div className="col-12 d-flex align-items-center justify-content-between mt-2 pt-2 border-top">
                                <div className="d-flex align-items-center gap-3">
                                    <Tooltip title="Attach Files" placement="top">
                                        <label htmlFor="project-attachment" style={{ cursor: 'pointer', margin: 0 }}>
                                            <div className="attachment-trigger">
                                                <span style={{ display:'inline-block', width:'16px', height:'16px', flexShrink:0, backgroundColor: '#1a1a1a', mask:"url('/assets/images/attachmentmodal.svg') no-repeat center / contain", WebkitMask:"url('/assets/images/attachmentmodal.svg') no-repeat center / contain", transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)', imageRendering: '-webkit-optimize-contrast' }} />
                                            </div>
                                        </label>
                                    </Tooltip>
                                    <input
                                        type="file"
                                        id="project-attachment"
                                        ref={fileInputRef}
                                        style={{ display: 'none' }}
                                        multiple
                                        onChange={handleFileChange}
                                    />

                                    {attachments.length > 0 && (
                                        <div className="d-flex align-items-center flex-wrap gap-2">
                                            {(showAllAttachments ? attachments : attachments.slice(0, 3)).map((file, idx) => (
                                                <div key={idx} className="attachment-preview-chip">
                                                    <i className="uil uil-file-alt" />
                                                    <span className="chip-file-name">{file.name}</span>
                                                    <i className="uil uil-times chip-remove-icon ms-1" onClick={() => removeAttachment(idx)} />
                                                </div>
                                            ))}
                                            {attachments.length > 3 && (
                                                <div
                                                    className="attachment-preview-chip cursor-pointer"
                                                    onClick={() => setShowAllAttachments(!showAllAttachments)}
                                                    style={{ background: '#f8f9fa', border: '1px dashed #adb5bd', color: '#1D1D1D' }}
                                                >
                                                    <span style={{ fontWeight: 600 }}>
                                                        {showAllAttachments ? "Show Less" : `+ ${attachments.length - 3} more`}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    className="btn-create-project"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <div className="spinner-border spinner-border-sm me-2" role="status" />
                                    ) : (
                                        <i className="uil uil-plus me-1" style={{ fontSize: '16px' }} />
                                    )}
                                    Create Project
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default CreateProject;
